//CRN_20261001 - ⚠️ ngOnInit() called for components/directives & not services ⚠️
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { AuditInsightsService } from '../system/audit/audit-insights-service';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from '../system/config-system/config-system-service';
import { ExceptionsService } from './exceptions-service';
import { filter, Subscription } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';
import { PageViewDTO } from 'src/app/core/models/system/audit/audit-insights.dto';

const BATCH_MAX        : number=50;
const FLUSH_AT_COUNT   : number=20;
const FLUSH_INTERVAL_MS: number=30000;
const MAX_DURATION_MS  : number=7200000;
const MAX_ROUTE_LENGTH : number=300;

interface CurrentView {
  route        : string;
  sent         : boolean;
  startedAt    : number;
  visibleMs    : number;
  visibleSince : number | null;
}

@Injectable({ providedIn: 'root' })

export class PageViewTrackerService {

  get enabled() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.AuditPageViewsEnabled, false); }

  private buffer           : PageViewDTO[]=[];
  private current          : CurrentView | null=null;
  private failed           : boolean=false;
  private flushTimer       : ReturnType<typeof setInterval> | null=null;
  private inFlight         : boolean=false;
  private routerSub        : Subscription | undefined;
  private visibilityHandler: (() => void) | null=null;

  constructor(
    private auditInsightsService: AuditInsightsService,
    private configSystemService : ConfigSystemService,
    private exceptionsService   : ExceptionsService,
    private ngZone              : NgZone,
    private router              : Router
  ) { }

  private beginView(route: string): void {
    try {
      const now=Date.now();

      this.current={
        route       : route,
        sent        : false,
        startedAt   : now,
        visibleMs   : 0,
        visibleSince: document.visibilityState==='visible' ? now : null
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildRouteTemplate(): string {
    try {
      const segments: string[]=[];
      let   node    : ActivatedRouteSnapshot | null=this.router.routerState.snapshot.root;

      while (node) {
        const path=node.routeConfig?.path ?? '';

        if (path.length>0)
          segments.push(path);

        node=node.firstChild;
      }

      return ('/' + segments.join('/')).substring(0, MAX_ROUTE_LENGTH);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '/';
    }
  }

  private endView(): void {
    try {
      if (!this.current || this.current.sent)
        return;

      this.pauseVisibleClock();

      this.buffer.push({
        durationMs : Math.min(Math.max(0, Math.round(this.current.visibleMs)), MAX_DURATION_MS),
        enteredDate: new Date(this.current.startedAt).toISOString(),
        route      : this.current.route
      });

      this.current.sent=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private flush(): void {
    try {
      if (this.failed || this.inFlight || this.buffer.length===0)
        return;

      if (!this.enabled) {
        this.buffer=[];
        return;
      }

      const batch=this.buffer.splice(0, BATCH_MAX);

      this.inFlight=true;

      this.auditInsightsService.logPageViews(batch)
      .subscribe({
        next : () => {
          this.inFlight=false;
        },
        error: error => {
          this.inFlight=false;
          this.failed  =true;
          this.buffer  =[];
          this.stop();
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private onNavigationEnd(): void {
    try {
      if (!this.enabled || this.failed)
        return;

      const route=this.buildRouteTemplate();

      if (this.current && this.current.route===route && !this.current.sent)
        return;

      this.endView();
      this.beginView(route);

      if (this.buffer.length>=FLUSH_AT_COUNT)
        this.flush();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private onVisibilityChange(): void {
    try {
      if (!this.current)
        return;

      if (document.visibilityState==='hidden') {
        this.endView();
        this.flush();
        return;
      }

      if (!this.current.sent && this.current.visibleSince==null)
        this.current.visibleSince=Date.now();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private pauseVisibleClock(): void {
    try {
      if (this.current?.visibleSince != null) {
        this.current.visibleMs   +=Date.now() - this.current.visibleSince;
        this.current.visibleSince=null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public start(): void {
    try {
      if (this.routerSub || this.failed || !this.enabled)
        return;

      this.beginView(this.buildRouteTemplate());

      this.routerSub=this.router.events
                     .pipe(filter(event => event instanceof NavigationEnd))
                     .subscribe(() => this.onNavigationEnd());

      this.ngZone.runOutsideAngular(() => {
        this.flushTimer       =setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
        this.visibilityHandler=() => this.onVisibilityChange();
        document.addEventListener('visibilitychange', this.visibilityHandler);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public stop(): void {
    try {
      this.routerSub?.unsubscribe();
      this.routerSub=undefined;

      if (this.flushTimer)
        clearInterval(this.flushTimer);

      this.flushTimer=null;

      if (this.visibilityHandler)
        document.removeEventListener('visibilitychange', this.visibilityHandler);

      this.visibilityHandler=null;
      this.current          =null;

      if (!this.inFlight)
        this.buffer=[];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
